import { Component } from '@theme/component';

/**
 * Bamboo benefits — two independent CSS marquees (gallery on top, benefits below).
 *
 * Both block types come out of a single `{% content_for 'blocks' %}`, so the section prints the
 * captured blocks into two lists and hides the wrong type in each with CSS. Each list is rendered
 * once and duplicated here so the `-50%` keyframe loops seamlessly. Exactly one duplicate is made
 * per list — each loop duration comes from "seconds per item × number of items", so extra copies
 * would multiply the perceived speed instead of preserving it.
 *
 * The two rows keep their own speeds; the component only pauses/suspends, it never drives layout.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [tracks]
 * @property {HTMLElement[]} [groups]
 *
 * @extends {Component<Refs>}
 */
export class BambooBenefitsComponent extends Component {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  #inViewport = true;

  /** Index of the block selected in the theme editor, or -1. */
  #selected = -1;

  connectedCallback() {
    super.connectedCallback();

    this.#duplicate();
    this.#listen();
    this.#observe();
    this.#listenToEditor();
    this.#updatePlayState();
  }

  /** Re-runs after a Section Rendering API re-render + morph, which drops the duplicates. */
  updatedCallback() {
    super.updatedCallback();

    this.#duplicate();
    this.#updatePlayState();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;

    this.#observer?.disconnect();
    this.#observer = null;

    super.disconnectedCallback();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /* -------------------------------------------------------------------------- */
  /* Public API — the shape `assets/theme-editor.js` calls on a slideshow.       */
  /* -------------------------------------------------------------------------- */

  /** Stops both scrolling animations. */
  pause() {
    this.paused = true;
  }

  /** Restarts both scrolling animations. */
  resume() {
    this.paused = false;
  }

  /**
   * Holds both rows still so the selected block can be read.
   *
   * @param {number|string} index - The index of the block to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Unused; kept for signature parity.
   */
  select(index, event, options = {}) {
    const target = Number(index);

    this.#selected = Number.isFinite(target) ? target : -1;
    this.pause();
  }

  /* -------------------------------------------------------------------------- */
  /* Layout                                                                     */
  /* -------------------------------------------------------------------------- */

  /** Makes every track hold exactly two copies of its authored list. Safe to call repeatedly. */
  #duplicate() {
    const tracks = this.refs.tracks ?? [];
    const groups = this.refs.groups ?? [];

    for (const [index, track] of tracks.entries()) {
      const group = groups[index];

      if (!track || !group || !group.isConnected || group.parentElement !== track) continue;

      for (const child of Array.from(track.children)) {
        if (child !== group) child.remove();
      }

      track.appendChild(this.#cloneGroup(group));
    }
  }

  /**
   * @param {Element} node
   * @returns {Element}
   */
  #cloneGroup(node) {
    const clone = /** @type {Element} */ (node.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');

    for (const element of [clone, ...Array.from(clone.querySelectorAll('*'))]) {
      element.removeAttribute('ref');
      element.removeAttribute('id');

      for (const attribute of Array.from(element.attributes)) {
        if (attribute.name.startsWith('data-shopify-editor')) element.removeAttribute(attribute.name);
      }
    }

    // The duplicate is decorative: keep it out of the tab order and off the priority queue.
    for (const link of Array.from(clone.querySelectorAll('a'))) {
      link.setAttribute('tabindex', '-1');
    }

    for (const image of Array.from(clone.querySelectorAll('img'))) {
      image.setAttribute('loading', 'lazy');
      image.removeAttribute('fetchpriority');
    }

    return clone;
  }

  /* -------------------------------------------------------------------------- */
  /* Events                                                                     */
  /* -------------------------------------------------------------------------- */

  #listen() {
    this.#abort?.abort();
    this.#abort = new AbortController();

    const { signal } = this.#abort;

    document.addEventListener('visibilitychange', () => this.#updatePlayState(), { signal });
  }

  #observe() {
    if (!('IntersectionObserver' in window)) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) this.#inViewport = entry.isIntersecting;

        this.#updatePlayState();
      },
      { rootMargin: '120px 0px' }
    );

    this.#observer.observe(this);
  }

  /**
   * `assets/theme-editor.js` only knows about `slideshow-component`, and it is overwritten on
   * every theme update — so this component carries its own listener (HORIZON-SPEC 10.2).
   */
  #listenToEditor() {
    if (!window.Shopify?.designMode) return;

    const signal = this.#abort?.signal;

    document.addEventListener(
      'shopify:block:select',
      (event) => {
        if (!(event.target instanceof HTMLElement)) return;

        const item = event.target.closest('.bs-bamboo__slide, .bs-bamboo__benefit');
        if (!item || item.closest('bamboo-benefits-component') !== this) return;

        this.select(item.getAttribute('data-index') ?? -1, event);
      },
      { signal }
    );

    document.addEventListener(
      'shopify:block:deselect',
      (event) => {
        if (!(event.target instanceof HTMLElement)) return;
        if (event.target.closest('bamboo-benefits-component') !== this) return;

        this.#selected = -1;
        this.resume();
      },
      { signal }
    );
  }

  /** Suspends both animations off-screen and in a hidden tab, without touching the public paused state. */
  #updatePlayState() {
    const suspended = document.hidden || !this.#inViewport;

    if (suspended) {
      this.setAttribute('data-suspended', '');
    } else {
      this.removeAttribute('data-suspended');
    }
  }
}

if (!customElements.get('bamboo-benefits-component')) {
  customElements.define('bamboo-benefits-component', BambooBenefitsComponent);
}
